"use client";

import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faCheck, faExclamation, faInfo, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "motion/react";
import { type ComponentProps, type ReactNode, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { cn } from "../..";
import { Typography } from "./typography/Typography";

interface AlertProps extends ComponentProps<"div"> {
	variant?: "info" | "warning" | "danger" | "success";
	dismissible?: boolean;
	timeout?: number;
	icon?: IconProp;
	border?: boolean;
	children: ReactNode;
}

const notificationVariants = {
	warning: { icon: faExclamation, color: "warning" },
	danger: { icon: faTimes, color: "danger" },
	success: { icon: faCheck, color: "success" },
	info: { icon: faInfo, color: "primary" },
};

export function Alert(props: AlertProps) {
	const { variant = "success", dismissible, timeout = 0, icon, border = true, children } = props;
	const { icon: iconVariant, color } = notificationVariants[variant];
	const defaultIcon = icon || (iconVariant as IconProp);

	const bgColor = cn({
		"bg-warning": color === "warning",
		"bg-danger": color === "danger",
		"bg-success": color === "success",
		"bg-primary": color === "primary",
	});

	const textColor = cn({
		"text-warning": color === "warning",
		"text-danger": color === "danger",
		"text-success": color === "success",
		"text-primary": color === "primary",
	});

	const [isVisible, setIsVisible] = useState<boolean>(true);
	const intl = useIntl();

	useEffect(() => {
		if (timeout) {
			const visibleTimeout = setTimeout(() => setIsVisible(false), timeout);

			return () => {
				clearTimeout(visibleTimeout);
			};
		}
	}, [timeout]);

	if (!isVisible) {
		return null;
	}

	return (
		<div
			className={cn("flex overflow-hidden rounded border bg-white", textColor, props.className, {
				"border-transparent": !border,
				"border-current": border,
			})}
		>
			<div className={cn("flex items-center justify-center p-4 text-white ", bgColor)}>
				<FontAwesomeIcon icon={defaultIcon} fixedWidth />
			</div>

			<div className="w-full">
				<div
					className={cn("flex", {
						"h-[calc(100%-3px)]": !!timeout,
						"h-full": !timeout,
					})}
				>
					<div className="inline-flex w-full flex-col justify-center px-4 py-2 leading-tight">
						{typeof children === "string" ? (
							<Typography variant="paragraph">{children}</Typography>
						) : (
							children
						)}
					</div>
					{(dismissible || !!timeout) && (
						<button
							type="button"
							className="ml-auto flex items-center justify-center p-4 text-shades-dark hover:bg-shades-light focus:outline-none focus-visible:bg-shades-light motion-safe:transition"
							onClick={() => setIsVisible(false)}
							title={intl.formatMessage({ defaultMessage: "Sluiten", id: "83CHTJ" })}
						>
							<FontAwesomeIcon icon={faTimes} fixedWidth />
						</button>
					)}
				</div>
				{!!timeout && (
					<div className="h-[3px] w-full">
						<motion.div
							initial={{ scaleX: 0, originX: 0 }}
							animate={{ scaleX: "100%", originX: 0 }}
							transition={{ duration: timeout / 1000 }}
							className={cn("h-full", bgColor)}
						/>
					</div>
				)}
			</div>
		</div>
	);
}
